import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Truck, ShieldCheck, Star, ImageIcon, Pencil, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import QuoteDialog, { partImageUrl } from "@/components/QuoteDialog";
import EditPartDialog from "@/components/EditPartDialog";
import { api } from "@/lib/api";
import { authFetch } from "@/lib/auth";
import { partSlug } from "@/lib/slug";

const conditionStyle = (c) => {
  if (c === "new") return "bg-emerald-600/20 text-emerald-400 border-emerald-700/40";
  if (c === "used") return "bg-amber-500/15 text-amber-400 border-amber-700/40";
  return "bg-blue-500/15 text-blue-400 border-blue-700/40";
};

const formatRand = (n) =>
  new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(n || 0);

export default function PartCard({ part, isBest, index, owned = false, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const firstImage = (part.image_paths && part.image_paths[0]) || null;

  const onDelete = async () => {
    if (!window.confirm(`Delete listing "${part.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/parts/${part.id}`, authFetch());
      toast.success("Listing deleted");
      onChange?.();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  return (
    <>
      <div
        data-testid={isBest ? "best-match-card" : `supplier-card-${index ?? 0}`}
        className={`relative bg-neutral-50 border ${
          isBest ? "border-[#FF5100] glow-orange" : "border-neutral-200"
        } p-4 hover-lift rise-in`}
        style={{ animationDelay: `${(index || 0) * 60}ms` }}
      >
        {isBest && (
          <div className="absolute -top-2.5 left-3 px-2 py-0.5 bg-[#FF5100] text-white text-[10px] font-bold tracking-[0.2em] font-mono-tech">
            BEST MATCH
          </div>
        )}

        <div className="flex gap-3">
          <div className="shrink-0 w-16 h-16 border border-neutral-200 bg-white grid place-items-center overflow-hidden">
            {firstImage ? (
              <img
                src={partImageUrl(firstImage)}
                alt={part.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <ImageIcon size={20} className="text-neutral-700" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="font-display text-2xl font-extrabold leading-none">
                {formatRand(part.price_zar)}
              </div>
              <span
                className={`shrink-0 px-2 py-1 text-[10px] uppercase tracking-wider font-bold border ${conditionStyle(
                  part.condition
                )}`}
              >
                {part.condition}
              </span>
            </div>
            <div className="mt-1 text-xs uppercase tracking-wider text-neutral-500 font-mono-tech">
              {part.category}
            </div>
          </div>
        </div>

        <div className="mt-3">
          <div className="font-display text-xl font-bold leading-tight">{part.name}</div>
          <div className="text-sm text-neutral-700">
            {part.make} {part.model} · {part.engine}
            {part.year_from ? ` · ${part.year_from}-${part.year_to}` : ""}
          </div>
        </div>

        {part.oem_number && (
          <div className="mt-2 font-mono-tech text-xs text-neutral-500">
            OEM <span className="text-white">{part.oem_number}</span>
          </div>
        )}

        {part.compatibility_notes && (
          <div className="mt-2 text-xs text-neutral-500 line-clamp-2">
            {part.compatibility_notes}
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-neutral-200 flex items-center gap-3 flex-wrap text-xs">
          <div className="flex items-center gap-1.5 text-neutral-700">
            <span className="font-semibold">{part.supplier_name}</span>
          </div>
          <div className="flex items-center gap-1 text-neutral-500">
            <MapPin size={12} /> {part.supplier_city}
            {typeof part.distance_km === "number" && (
              <span className="text-[#FF5100] ml-1 font-mono-tech">{part.distance_km}km</span>
            )}
          </div>
          {part.supplier_rating && (
            <div className="flex items-center gap-1 text-neutral-500">
              <Star size={12} className="text-[#FF5100]" fill="#FF5100" />
              {part.supplier_rating.toFixed(1)}
            </div>
          )}
          {part.warranty_months > 0 && (
            <div className="flex items-center gap-1 text-emerald-400">
              <ShieldCheck size={12} /> {part.warranty_months}mo warranty
            </div>
          )}
          {part.delivery_available && (
            <div className="flex items-center gap-1 text-neutral-500">
              <Truck size={12} /> Delivery
            </div>
          )}
        </div>

        <button
          type="button"
          data-testid={isBest ? "best-match-contact-button" : `supplier-contact-button-${index ?? 0}`}
          onClick={() => setOpen(true)}
          className="mt-3 w-full bg-[#FF5100] hover:bg-[#E64A00] text-white font-bold uppercase tracking-wider text-xs py-2.5 transition-colors"
        >
          Contact Supplier
        </button>

        <Link
          to={`/parts/${partSlug(part)}`}
          data-testid={`part-detail-link-${index ?? 0}`}
          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 border border-neutral-200 hover:border-neutral-900 text-neutral-700 hover:text-neutral-900 text-xs uppercase tracking-wider py-2 transition-colors"
        >
          View details <ExternalLink size={11} />
        </Link>

        {owned && (
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              data-testid={`part-edit-${index ?? 0}`}
              onClick={() => setEditing(true)}
              className="flex-1 flex items-center justify-center gap-1.5 border border-neutral-200 hover:border-neutral-300 text-neutral-700 text-xs uppercase tracking-wider py-2 transition-colors"
            >
              <Pencil size={12} /> Edit
            </button>
            <button
              type="button"
              data-testid={`part-delete-${index ?? 0}`}
              onClick={onDelete}
              className="flex items-center justify-center gap-1.5 border border-neutral-200 hover:border-red-300 hover:text-red-600 text-neutral-700 text-xs uppercase tracking-wider py-2 px-3 transition-colors"
            >
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>
      {open && <QuoteDialog part={part} onClose={() => setOpen(false)} />}
      {editing && (
        <EditPartDialog
          part={part}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChange?.(); }}
        />
      )}
    </>
  );
}
